<template>
  <div class="image-annotation-toolbar">
    <!-- back button -->
    <tooltip-button
      class="btn-back"
      kind="ghost"
      :tip="t('image_annotation_toolbar_tooltip_back')"
      alignment="center"
      direction="bottom"
      icon-only
      :disabled="false"
      @click="handleBackButtonClicked"
    >
      <svg-icon name="ArrowLeft24" kind="action" />
    </tooltip-button>

    <!-- Undo button -->
    <tooltip-button
      class="btn-undo"
      kind="ghost"
      :tip="t('image_annotation_toolbar_tooltip_undo')"
      alignment="center"
      direction="bottom"
      icon-only
      :disabled="!isCanvasDirty"
      @click="handleUndoButtonClicked"
    >
      <svg-icon name="Undo24" kind="action" />
    </tooltip-button>

    <!-- Select tool button -->
    <tooltip-button
      class="btn-select"
      :class="{ active: selectButtonClicked }"
      kind="ghost"
      :tip="t('image_annotation_toolbar_tooltip_select')"
      alignment="center"
      direction="bottom"
      icon-only
      :disabled="!isCanvasDirty"
      @click="handleSelectButtonClicked"
    >
      <svg-icon name="Select24" kind="action" />
    </tooltip-button>

    <!-- Draw button -->
    <tooltip-button
      class="btn-draw"
      :class="{ active: selectedTool === 'draw' }"
      kind="ghost"
      :tip="t('image_annotation_toolbar_tooltip_draw')"
      alignment="center"
      direction="bottom"
      icon-only
      :disabled="false"
      @click="handleToolSelected($event, 'draw')"
    >
      <svg-icon name="Pencil24" kind="action" />
    </tooltip-button>

    <!-- Stamp button -->
    <tooltip-button
      class="btn-stamp"
      :class="{ active: selectedTool === 'stamp' }"
      kind="ghost"
      :tip="t('image_annotation_toolbar_tooltip_stamp')"
      alignment="center"
      direction="bottom"
      icon-only
      :disabled="false"
      @click="handleToolSelected($event, 'stamp')"
    >
      <svg-icon name="Stamp24" kind="action" />
    </tooltip-button>

    <!-- Text button -->
    <tooltip-button
      class="btn-text"
      :class="{ active: selectedTool === 'text' }"
      kind="ghost"
      :tip="t('image_annotation_toolbar_tooltip_text')"
      alignment="center"
      direction="bottom"
      icon-only
      :disabled="false"
      @click="handleToolSelected($event, 'text')"
    >
      <svg-icon name="Text24" kind="action" />
    </tooltip-button>

    <!-- Done button -->
    <cv-button
      class="btn-done"
      size="sm"
      :disabled="!isCanvasDirty"
      @click="handleDoneButtonClicked"
    >
      {{ t("image_annotation_toolbar_tooltip_done") }}
    </cv-button>
  </div>

  <image-annotation-config-popup
    ref="configPopup"
    :visible="showConfigPopup"
    :reference-element="canvasAnchorElement ?? referenceElement"
    :selected-tool="selectedTool"
    :selected-color="selectedColor"
    :selected-thickness="selectedThickness"
    :selected-stamp="selectedStamp"
    @on-color-change="handleColorChange"
    @on-thickness-change="handleThicknessChange"
    @on-stamp-selected="handleStampSelected"
    @close="handleConfigPopupClose"
  />
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, shallowRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useModal } from "@plugins/modal/composables/modal-instance.ts";
import TooltipButton from "@plugins/common/components/TooltipButton.vue";
import { FabricObject } from "fabric";
import ImageAnnotationConfigPopup from "@modules/annotation/components/ImageAnnotationConfigPopup.vue";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import type EventBus from "@plugins/common/service/eventbus";
import { useDirty } from "@plugins/common/composables/page-dirty";
import { useImageAnnotationDefaults } from "@modules/annotation/composables/use-image-annotation-defaults.ts";
import { IMAGE_ANNOTATION_TOOL } from "@/modules/annotation/service/annotation.constant";

type ImageAnnotationTool = "draw" | "stamp" | "text";

interface Props {
  /** Bus shared only with the canvas of the same image. */
  eventBus: EventBus;
  /** Id used to flag the page as dirty while the canvas has unsaved elements. */
  dirtyId?: string;
}

const props = withDefaults(defineProps<Props>(), {
  dirtyId: "image-annotation",
});

const { t } = useI18n();
const modal = useModal();
const { defaultColor, defaultThickness } = useImageAnnotationDefaults();
const { setDirty, removeDirty } = useDirty(() => props.dirtyId);

const emit = defineEmits<{
  back: [annotationObjects: FabricObject[] | null];
}>();

// Event handlers are registered during setup, so Vue stops them when the component unmounts.
props.eventBus.$on(
  "canvasModified",
  (payload: { modified: boolean; annotationObjects: FabricObject[] }) => {
    isCanvasDirty.value = payload.modified;
    annotationObjects.value = payload.annotationObjects;
  },
);

props.eventBus.$on("drawStart", () => {
  if (selectedTool.value === "draw") {
    resetSelectedTool();
  }
});

props.eventBus.$on("stampPlaced", () => {
  if (selectedTool.value === "stamp") {
    resetSelectedTool();
  }
});

props.eventBus.$on("textStart", () => {
  if (selectedTool.value === "text") {
    resetSelectedTool();
    selectButtonClicked.value = true;
  }
});

props.eventBus.$on("canvasReady", (payload: { element: HTMLElement }) => {
  canvasAnchorElement.value = payload.element;
});

const isCanvasDirty = ref(false);

const selectedTool = ref<ImageAnnotationTool | null>(null);
// Pen and text colours are kept separately and for the whole annotation session
const penColor = ref<string>(defaultColor.value);
const textColor = ref<string>(defaultColor.value);
const selectedThickness = ref<number | null>(defaultThickness);
const selectedStamp = ref<string | null>(null);

const showConfigPopup = ref(false);
const referenceElement = ref<HTMLElement | null>(null);
const canvasAnchorElement = shallowRef<HTMLElement | null>(null);
const annotationObjects = shallowRef<FabricObject[] | null>(null);
const selectButtonClicked = ref(false);

const selectedColor = computed<string>(() =>
  selectedTool.value === "text" ? textColor.value : penColor.value,
);

// Flag the page as dirty while there are unsaved elements, so navigating away prompts the user
watch(isCanvasDirty, (dirty) => {
  if (dirty) {
    setDirty(props.dirtyId);
  } else {
    removeDirty(props.dirtyId);
  }
});

watch(selectedTool, (newTool, oldTool) => {
  // Only trigger re-positioning if popup was visible and tool changed
  if (showConfigPopup.value && oldTool !== null && newTool !== oldTool) {
    showConfigPopup.value = false;
    nextTick(() => {
      showConfigPopup.value = true;
    });
  }
});

onBeforeUnmount(() => {
  removeDirty(props.dirtyId);
});

function resetSelectedTool(): void {
  showConfigPopup.value = false;
  selectedTool.value = null;
}

function handleBackButtonClicked(): void {
  if (isCanvasDirty.value) {
    const modalProps = {
      title: t("image_annotation_toolbar_modal_title"),
      content: t("image_annotation_toolbar_modal_subtitle"),
      primaryBtnLabel: t("button_close"),
      actionPrimary: () => {
        emit("back", null);
      },
    };
    modal.showMessage("warning", modalProps);
    return;
  }

  emit("back", null);
}

function handleUndoButtonClicked(event: MouseEvent): void {
  event.preventDefault();
  (event.currentTarget as HTMLButtonElement).blur();
  selectButtonClicked.value = false;
  props.eventBus.$emit("undo");
}

const handleSelectButtonClicked = (event: MouseEvent) => {
  event.preventDefault();
  (event.currentTarget as HTMLButtonElement).blur();

  selectButtonClicked.value = true;
  showConfigPopup.value = false;

  selectedTool.value = null;
  selectedStamp.value = null;
  props.eventBus.$emit("toolSelected", IMAGE_ANNOTATION_TOOL.SELECT);
};

function handleToolSelected(event: MouseEvent, tool: ImageAnnotationTool): void {
  event.preventDefault();
  selectButtonClicked.value = false;
  selectedTool.value = tool;

  const button = event.currentTarget as HTMLButtonElement;
  button.blur();
  selectedStamp.value = null;
  props.eventBus.$emit("toolSelected", tool);

  if (tool === "draw" || tool === "stamp" || tool === "text") {
    referenceElement.value = button;
    showConfigPopup.value = true;
    return;
  }

  showConfigPopup.value = false;
}

function handleColorChange(color: string): void {
  switch (selectedTool.value) {
    case "draw":
      penColor.value = color;
      props.eventBus.$emit("penColorChange", color);
      break;
    case "text":
      textColor.value = color;
      showConfigPopup.value = false;
      props.eventBus.$emit("textColorChange", color);
      break;
  }
}

function handleThicknessChange(thickness: number): void {
  selectedThickness.value = thickness;
  props.eventBus.$emit("penWidthChange", thickness);
}

function handleStampSelected(stampShape: string): void {
  selectedStamp.value = stampShape;
  showConfigPopup.value = false;
  props.eventBus.$emit("stampChange", stampShape);
}

function handleDoneButtonClicked(): void {
  props.eventBus.$emit("done");
}

function handleConfigPopupClose(): void {
  showConfigPopup.value = false;
}
</script>

<style lang="scss">
@use "@styles/themes/sls-theme/tokens/colors" as *;
@use "@styles/themes/sls-theme/tokens/spacing" as *;

.image-annotation-toolbar {
  display: flex;
  align-items: center;
  padding: $spacing-03;
  gap: $spacing-03;
  flex-wrap: wrap;

  .tooltip-button.bx--btn--icon-only {
    width: 32px;
    height: 32px;

    &[disabled] {
      .sls-icon {
        path {
          fill: $disabled-01;
        }
      }
    }

    &.active {
      background-color: $selected-ui;
    }

    .sls-icon {
      path {
        fill: $icon-02;
      }
    }
  }

  .cv-button.btn-done {
    width: 65px;
    min-width: 65px;
    white-space: nowrap;
  }

  .btn-back {
    margin-right: auto;
  }
}
</style>
